/**
 * Company: SPHMedia
 * Description: This is the base page component for all BH pages
 */

import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import { MainContainerNew } from "@components/Blocks/Container/MainContainerNew";
import CatfishAd from "@components/Elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@components/Elements/Advertisement/helpers";
import PrestitialAd from "@components/Elements/Advertisement/PrestitialAd";
import { FooterNew } from "@components/Modules/FooterNew/FooterNew";
import FooterNewV2 from "@components/Modules/FooterNew/FooterNewV2";
import { HeaderNew } from "@components/Modules/HeaderNew/HeaderNew";
import { HeaderNewV2 } from "@components/Modules/HeaderNew/HeaderNewV2";
import {
  BackToQuickNews,
  QnFloatButton,
} from "@components/Modules/QuickNews/QnButtons";
import { pubPageAdTargeting } from "@config/ads";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import {
  CAS_EI_MODEL_ID,
  ENABLE_NEW_HEADER_FOOTER,
  IS_PLAYWRIGHT_TEST,
  QUICKNEWS_FLAG,
} from "@helper/getEnvVariables";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGlobalMediaControl } from "@hooks/useGlobalMediaControl";
import { useHideInvisibleAds } from "@hooks/useHideInvisibleAds";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetValue,
} from "@typings/Ads.d";
import type { GADataProps } from "@typings/GA";
import type { MetaTagsProps } from "@typings/MetaTags";
import { ReactElement } from "react";
import { useLocation } from "react-router";

export type AdsSettings = {
  isPrestitialEnabled: boolean | undefined;
  isCatfishEnabled: boolean | undefined;
  topOverlayImpressions: number;
  topOverlayValidity: number;
  isTaboolaEnabled: boolean | undefined;
};

export type BHArticleBasePageProps = {
  adsUniqueSectionName?: string;
  children?: (adsSettings: AdsSettings) => React.ReactNode;
  hasLb1Ads?: boolean;
  gaData?: GADataProps;
  metaTags?: MetaTagsProps;
  pageAdTargetValue?: PageAdTargetValue;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  prestitialPageTargeting?: PageAdTargetValue;
  showNDRBanner?: boolean;
  articleId?: string;
};

/**
 * BHBasePage
 * Description: The base page component for all BH pages
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function BHArticleBasePage({
  adsUniqueSectionName,
  children,
  hasLb1Ads = true,
  pageAdTargetValue,
  gsChannels,
  articleSlotTarget,
  prestitialPageTargeting,
  showNDRBanner = true,
  articleId,
}: BHArticleBasePageProps): ReactElement {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const refCode = searchParams.get("ref");

  useNeuronPageViewPush();
  const adsSettings: AdsSettings = useGlobalAdSetting();
  useGlobalMediaControl();
  useHideInvisibleAds();

  const pageAdTarget =
    pageAdTargetValue &&
    (pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
      string,
      PageAdTargetValue
    >);
  const prestitialPageAdTarget =
    prestitialPageTargeting &&
    (pubPageAdTargeting[prestitialPageTargeting] as AdTargetingType<
      string,
      PageAdTargetValue
    >);

  const lb1PageNumberSlotTarget: AdTargetingType<ArticleAdTargetKeys, string> =
    {
      key: ArticleAdTargetKeys.PAGE_NUMBER,
      value: "1",
    };

  return (
    <>
      <MainContainerNew>
        <>
          {refCode === "quick-news" && <BackToQuickNews />}
          <AdsContainer className="flex justify-center border-b-[1px] border-border-default">
            {hasLb1Ads && (
              <AdsLb1
                uniqueSectionName={adsUniqueSectionName}
                {...(pageAdTargetValue && {
                  pageAdTargetType: pageAdTargetValue,
                })}
                hasStripeBg={false}
                wrapperStyle="mb-0"
                isArticlePage={true}
                gsChannels={gsChannels}
                articleSlotTarget={[
                  ...(articleSlotTarget ?? []),
                  lb1PageNumberSlotTarget,
                ]}
              />
            )}
          </AdsContainer>
          {refCode !== "quick-news" &&
            (ENABLE_NEW_HEADER_FOOTER ? (
              <HeaderNewV2
                showNDRBanner={showNDRBanner}
                hideNavigationInMobile={true}
              />
            ) : (
              <HeaderNew
                showNDRBanner={showNDRBanner}
                hideScrollableSectionMenu={true}
                articleId={articleId}
              />
            ))}

          {IS_PLAYWRIGHT_TEST !== "true" && QUICKNEWS_FLAG && (
            <QnFloatButton
              refCode={`article-quick-news`}
              isCatfishEnabled={adsSettings.isCatfishEnabled}
            />
          )}

          {IS_PLAYWRIGHT_TEST !== "true" && (
            <PrestitialAd
              slot={GoogleAdsSlotFactory.prestitial(adsUniqueSectionName)}
              isPrestitialEnabled={adsSettings.isPrestitialEnabled}
              {...(prestitialPageAdTarget && {
                pubAdTargeting: [prestitialPageAdTarget],
              })}
              topOverlayImpressions={adsSettings.topOverlayImpressions}
              topOverlayValidity={adsSettings.topOverlayValidity}
              gsChannels={gsChannels}
              articleSlotTarget={articleSlotTarget?.slice(0, 2)}
            />
          )}
          {typeof children === "function" ? children(adsSettings) : children}
        </>
      </MainContainerNew>
      {ENABLE_NEW_HEADER_FOOTER ? <FooterNewV2 /> : <FooterNew />}
      {adsSettings.isCatfishEnabled && (
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(adsUniqueSectionName)}
          isCatfishEnabled={adsSettings.isCatfishEnabled}
          {...(pageAdTarget && { pubAdTargeting: [pageAdTarget] })}
          gsChannels={gsChannels}
          articleSlotTarget={articleSlotTarget}
        />
      )}
      <CASBlock casId={CAS_EI_MODEL_ID} />
    </>
  );
}
